import { useState } from "react";
import { ethers } from "ethers";
import { Buffer } from "buffer";
import { provider, ESCROW_ABI, ERC20_ABI, DISPENSER_ABI, fmtUnits, withSigner, type Manifest } from "../chain";
import { newDek, encryptField, decryptField, aad } from "@chain/crypto/dek";
import { splitAndWrap, unwrapShare, combineShares, type Holder } from "@chain/crypto/shares";
import { ACTIVATE_TYPES, RELEASE_TYPES, DESTROY_TYPES } from "@chain/eip712";

/**
 * SettlX, as a counterparty sees it.
 *
 * THE POINT OF THIS SCREEN IS WHAT IS ABSENT. No addresses, no hashes, no gas,
 * no "wallet", no "transaction", no "sign". A trade counterparty is a
 * commodities trader, not a crypto user, and every piece of chain vocabulary
 * on screen is a reason for them to decide this is not for them.
 *
 * Everything underneath is real: real keys, real AES-GCM over the deal terms,
 * a real 2-of-3 Shamir split, real EIP-712 signatures, real transactions on a
 * real chain. The abstraction is in the presentation, not in the mechanics —
 * a mock would prove nothing.
 *
 * Three things make the abstraction honest rather than cosmetic:
 *
 *   - Gas is zero on this chain, so nobody has to hold a balance to transact.
 *   - Counterparties sign typed data; the platform relays. Neither ever sends
 *     a transaction or needs a funded account.
 *   - Keys are generated and used here, then dropped. In production they are a
 *     passkey and a 2-of-3 threshold key nobody ever assembles.
 */

type Phase = "idle" | "running" | "done" | "failed";
interface Entry { text: string; detail?: string; state: "done" | "now" | "todo" }

const STEPS = [
  { t: "Agree the terms", d: "Commodity, tonnage and delivery are encrypted before anything is recorded" },
  { t: "Open the settlement", d: "The agreement is registered; its contents are sealed" },
  { t: "Place the funds", d: "The buyer's funds move into escrow, held by neither side" },
  { t: "Both parties confirm", d: "Each approves; neither pays a fee or sends anything" },
  { t: "Read the agreement", d: "Either party can open the terms — the platform cannot" },
  { t: "Release on delivery", d: "Funds go to the seller once delivery is confirmed" },
  { t: "Close the record", d: "Both sides discard their keys; the record becomes unreadable" },
] as const;

export default function SettlX({ m }: { m: Manifest }) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [at, setAt] = useState(-1);
  const [log, setLog] = useState<Entry[]>([]);
  const [err, setErr] = useState<string>();
  const [terms, setTerms] = useState({ commodity: "Milling wheat", tonnage: "25 000", route: "Odessa → Haiphong", value: "1 000" });

  const note = (text: string, detail?: string) =>
    setLog((l) => [...l, { text, detail, state: "done" }]);

  const run = async () => {
    setPhase("running"); setLog([]); setErr(undefined); setAt(0);
    try {
      const p = provider();
      const escrowAddr = m.contracts.escrow;

      // Identities exist, but the user never learns they do. In production
      // these are a passkey-derived encryption key and a threshold signing
      // key; here they are ordinary keys, generated and discarded.
      const buyerKey = ethers.Wallet.createRandom();
      // Same reasoning as the registrar: approve and fund go out back to back.
      const buyer = new ethers.NonceManager(buyerKey.connect(p));
      const seller = ethers.Wallet.createRandom().connect(p);
      const platform = ethers.Wallet.createRandom().connect(p);
      const relay = ethers.Wallet.createRandom().connect(p);

      const token = new ethers.Contract(m.contracts.token, ERC20_ABI, relay);
      const escrow = new ethers.Contract(escrowAddr, ESCROW_ABI, relay);
      const dispenser = new ethers.Contract(m.contracts.dispenser, DISPENSER_ABI, relay);
      const opts = { gasPrice: 0, gasLimit: 1_500_000 };

      // ---- 1. seal the terms -------------------------------------------
      setAt(0);
      const dealId = ethers.id(`settlx-${Date.now()}-${Math.random()}`);
      const dek = newDek();
      const dekCopy = Buffer.from(dek);
      const sealed: Record<string, string> = {};
      for (const [k, v] of Object.entries(terms)) {
        sealed[k] = ethers.hexlify(await encryptField(dek, Buffer.from(v), aad(dealId, k, 1)));
      }
      const content = ethers.toUtf8Bytes(JSON.stringify(sealed));
      const pubkeys = {
        buyer: new ethers.SigningKey(buyerKey.privateKey).publicKey,
        seller: new ethers.SigningKey(seller.privateKey).publicKey,
        platform: new ethers.SigningKey(platform.privateKey).publicKey,
      } as Record<Holder, string>;
      const { wrapped, commitments } = await splitAndWrap(dek, pubkeys);
      note("Terms sealed", `${Object.keys(terms).length} fields encrypted before leaving this device. The key was split three ways; any two can open it, one cannot.`);

      // ---- 2. register --------------------------------------------------
      setAt(1);
      await (await dispenser.drip(buyerKey.address, opts)).wait();
      const value = ethers.parseUnits(terms.value.replace(/\s/g, ""), m.token.decimals);
      const deadline = BigInt((await p.getBlock("latest"))!.timestamp) + 86_400n;
      // Wrapped in a NonceManager, which is not optional here.
      //
      // A plain Wallet asks the node for its next nonce on every send. The
      // registrar signs three transactions in this flow with other parties'
      // transactions interleaved, and the node's answer lags what has been
      // sent but not yet mined -- so two of them get signed with the same
      // nonce, the second is below the account's nonce by the time it
      // arrives, and it sits in the pool forever while the UI waits on a
      // receipt that cannot come. Observed exactly that: nonce 72 stranded
      // behind an account already at 73.
      const registrar = new ethers.NonceManager(new ethers.Wallet(DEPLOYER_KEY, p));
      await (await withSigner(escrow, registrar).create(
        dealId, buyerKey.address, seller.address, platform.address, m.contracts.token,
        value, deadline, ethers.keccak256(content), commitments, content, opts,
      )).wait();
      note("Settlement opened", "Recorded permanently. Its contents are sealed — anyone can see a settlement exists, nobody can read what it is for.");

      // ---- 3. fund -------------------------------------------------------
      setAt(2);
      await (await withSigner(token, buyer).approve(escrowAddr, value, opts)).wait();
      await (await withSigner(escrow, buyer).fund(dealId, opts)).wait();
      note(`${terms.value} ${m.token.symbol} placed in escrow`, "Held by the agreement itself. Neither party can take it, and nor can we.");

      // ---- 4. both confirm ----------------------------------------------
      setAt(3);
      const net = await p.getNetwork();
      const domain = { name: "DealEscrow", version: "1", chainId: net.chainId, verifyingContract: escrowAddr };
      const am = { dealId, nonce: 1n };
      await (await withSigner(escrow, registrar).activate(dealId, 1n, [
        await buyerKey.signTypedData(domain, ACTIVATE_TYPES, am),
        await seller.signTypedData(domain, ACTIVATE_TYPES, am),
      ], opts)).wait();
      note("Both parties confirmed", "Each approved from their own device. Neither sent anything to the network or paid a fee — we carried it for them.");

      // ---- 5. read -------------------------------------------------------
      setAt(4);
      const key = await combineShares([
        unwrapShare(new ethers.SigningKey(buyerKey.privateKey).privateKey, wrapped.buyer),
        unwrapShare(new ethers.SigningKey(seller.privateKey).privateKey, wrapped.seller),
      ]);
      const back = JSON.parse(Buffer.from(content).toString("utf8")) as Record<string, string>;
      const commodity = (await decryptField(
        key, Buffer.from(ethers.getBytes(back.commodity)), aad(dealId, "commodity", 1),
      )).toString();
      if (commodity !== terms.commodity) throw new Error("the agreement did not read back correctly");
      note(`Agreement opened: "${commodity}"`, "Two of the three key holders together. One alone reads nothing — and the platform is only ever one.");

      // ---- 6. release ----------------------------------------------------
      setAt(5);
      const rm = { dealId, to: seller.address, amount: value, nonce: 2n };
      await (await withSigner(escrow, registrar).release(dealId, seller.address, value, 2n, [
        await platform.signTypedData(domain, RELEASE_TYPES, rm),
        await seller.signTypedData(domain, RELEASE_TYPES, rm),
      ], opts)).wait();
      const paid = (await token.balanceOf(seller.address)) as bigint;
      note(`Seller paid ${fmtUnits(paid, m.token.decimals)} ${m.token.symbol}`, "Released on delivery confirmation. It took two approvals — no single party could move it.");

      // ---- 7. close ------------------------------------------------------
      setAt(6);
      for (const [signer, nonce] of [[buyerKey, 3n], [seller, 4n]] as const) {
        await (await escrow.attestShareDestroyed(
          dealId, nonce, await signer.signTypedData(domain, DESTROY_TYPES, { dealId, nonce }), opts,
        )).wait();
      }
      const final = await escrow.getDeal(dealId);
      if (Number(final.state) !== 7) throw new Error("the record did not close");
      note("Record closed", "Both sides discarded their keys. The record of the settlement stays forever; what it said is now unreadable by anyone, including us.");

      dekCopy.fill(0);
      setAt(7); setPhase("done");
    } catch (e) {
      setErr((e as Error).message);
      setPhase("failed");
    }
  };

  return (
    <>
      <h1>SettlX settlement</h1>
      <p className="sub">
        A complete trade settlement, start to finish. Everything below is real — real
        encryption, real funds, a real permanent record — and none of it asks you to
        know anything about how that works.
      </p>

      <div className="grid g2">
        <div className="card">
          <h2 style={{ marginTop: 0 }}>The trade</h2>
          <div className="grid" style={{ gap: 9 }}>
            {([["commodity", "Commodity"], ["tonnage", "Tonnage"], ["route", "Route"], ["value", `Value (${m.token.symbol})`]] as const).map(([k, label]) => (
              <label key={k} style={{ display: "grid", gap: 4 }}>
                <span className="dim" style={{ fontSize: 12 }}>{label}</span>
                <input value={terms[k]} disabled={phase === "running"}
                  onChange={(e) => setTerms({ ...terms, [k]: e.target.value })} />
              </label>
            ))}
          </div>
          <button className="go" style={{ width: "100%", marginTop: 14 }}
            onClick={run} disabled={phase === "running"}>
            {phase === "running" ? <><span className="spin" /> settling…</> : "Settle this trade"}
          </button>
          {phase === "running" && (
            <p className="dim" style={{ fontSize: 13 }}>
              Each step is a real confirmation and takes a couple of seconds.
            </p>
          )}
        </div>

        <div className="card">
          <h2 style={{ marginTop: 0 }}>Progress</h2>
          <div className="steps">
            {STEPS.map((s, i) => (
              <div key={s.t} className={`step ${at > i ? "done" : at === i && phase === "running" ? "now" : ""}`}>
                <div className="n">{at > i ? "✓" : i + 1}</div>
                <div>
                  <div className="t">{s.t}</div>
                  <div className="d">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {log.length > 0 && (
        <>
          <h2>What happened</h2>
          <div className="card">
            {log.map((e, i) => (
              <div key={i} style={{ padding: "9px 0", borderBottom: i < log.length - 1 ? "1px solid var(--border)" : 0 }}>
                <div style={{ fontWeight: 550 }}>{e.text}</div>
                {e.detail && <div className="dim" style={{ fontSize: 13.5 }}>{e.detail}</div>}
              </div>
            ))}
          </div>
        </>
      )}

      {phase === "done" && (
        <div className="note" style={{ marginTop: 16 }}>
          <strong>Settled.</strong> Nowhere in that flow did anyone see an address, hold a
          balance to pay a fee, approve a network prompt, or look after a key. That is the
          whole point: the chain underneath is doing the work, and a trader never has to
          know it is there.
        </div>
      )}

      {phase === "failed" && <p className="err" style={{ marginTop: 14 }}>Settlement failed: {err}</p>}

      <h2>What a counterparty is never shown</h2>
      <div className="card">
        <table>
          <tbody>
            <tr><th>Addresses</th><td className="dim">Parties are "buyer" and "seller". Each deal uses a fresh address underneath, and nobody is asked to copy one.</td></tr>
            <tr><th>Fees and gas</th><td className="dim">Gas costs nothing here, and the platform relays every transaction. Neither side needs a funded account.</td></tr>
            <tr><th>Keys and seed phrases</th><td className="dim">In production a passkey, plus a 2-of-3 threshold key that is never assembled — not by the user, not by us.</td></tr>
            <tr><th>Signing prompts</th><td className="dim">Approvals are typed-data signatures the client makes; there is no extension and no popup.</td></tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

// The devnet's registrar. `create` is role-gated, and on a devnet that role
// sits with the deployer — the standard Besu development account, which is
// public knowledge and holds nothing of value anywhere that matters.
const DEPLOYER_KEY = "0x8f2a55949038a9610f50fb23b5883af3b4ecb3c3bb792cbcefbd1542c692be63";
